// QuestionsPage — the Question Board (task #15): the DISCOVERY leg beside goals/actionables.
// Kanban columns driven by data, not calendar: Abiertas (unbound) · Midiendo (gathering) ·
// Respondibles (the posterior cleared the bar — incl. "sin cambio real", the answered-null) ·
// Respondidas. The consultant harvests explicitly; nothing auto-answers.
import { useState, useEffect } from 'react'
import {
  Button, Tag, Card, Input, Label, Modal, Textarea, Icon, Spinner, Title, Text,
} from '@nimbus-ds/components'
import { PlusIcon, QuestionCircleIcon } from '@nimbus-ds/icons'
import { useToast } from '@/hooks/use-toast'
import { questionService, type Question } from '@/api/questions'
import { authService } from '@/api/auth'

const COLUMNS: Array<{ key: string; title: string; hint: string; match: (q: Question) => boolean }> = [
  { key: 'open', title: 'Abiertas', hint: 'Sin instrumento todavía', match: (q) => q.status === 'open' },
  { key: 'gathering', title: 'Midiendo', hint: 'Juntando datos — el posterior decide cuándo', match: (q) => q.status === 'gathering' && q.preview?.state !== 'answerable' },
  { key: 'answerable', title: 'Respondibles', hint: 'Los datos ya alcanzan para responder', match: (q) => q.status === 'gathering' && q.preview?.state === 'answerable' },
  { key: 'answered', title: 'Respondidas', hint: 'Cosechadas — alimentan las próximas acciones', match: (q) => q.status === 'answered' },
]

const VERDICT_LABEL: Record<string, { label: string; tone: 'success' | 'warning' | 'neutral' | 'primary' }> = {
  moved_up: { label: 'Subió', tone: 'success' },
  moved_down: { label: 'Bajó', tone: 'warning' },
  no_meaningful_change: { label: 'Sin cambio real', tone: 'primary' },
  manual: { label: 'Respondida', tone: 'neutral' },
}

const pct = (v?: number | null) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`)

function QuestionCard({ q, onHarvest, onKill }: {
  q: Question; onHarvest: (q: Question) => void; onKill: (q: Question) => void
}) {
  const pv = q.preview
  const answered = q.status === 'answered'
  const verdict = answered ? q.answer?.verdict : pv?.verdict
  return (
    <Card>
      <Card.Body>
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <Text fontWeight="medium" color="neutral-textHigh">{q.question}</Text>
            {verdict && VERDICT_LABEL[verdict] && (
              <Tag appearance={VERDICT_LABEL[verdict].tone}>{VERDICT_LABEL[verdict].label}</Tag>
            )}
          </div>
          {q.indicator && (
            <Text fontSize="caption">
              {q.indicator.label || q.indicator.binding.metric}
              {pv?.current != null && ` · ahora ${pct(pv.current)}`}
              {pv?.baseline != null && ` · al preguntar ${pct(pv.baseline)}`}
            </Text>
          )}
          {pv?.state === 'gathering' && pv.p_inside != null && (
            <Text fontSize="caption">
              P(sin cambio) {(pv.p_inside * 100).toFixed(0)}% · P(subió) {((pv.p_better ?? 0) * 100).toFixed(0)}% ·
              P(bajó) {((pv.p_worse ?? 0) * 100).toFixed(0)}% — falta llegar a {(pv.threshold! * 100).toFixed(0)}%
            </Text>
          )}
          {pv?.state === 'informational' && (
            <Text fontSize="caption">
              {q.indicator?.binding.metric}: {pv.baseline} → {pv.current}
              {pv.delta_pct != null && ` (${pv.delta_pct > 0 ? '+' : ''}${pv.delta_pct}%)`} — métrica de conteo, se responde a criterio
            </Text>
          )}
          {q.evidence?.summary && <Text fontSize="caption">{q.evidence.summary}</Text>}
          {answered && q.answer?.summary && <Text fontSize="caption">✓ {q.answer.summary}</Text>}
          <div className="flex items-center justify-between">
            <Text fontSize="caption">{pv?.age_days ?? 0} días</Text>
            {!answered && (
              <div className="flex gap-1">
                <Button appearance="transparent" onClick={() => onKill(q)}>Matar</Button>
                <Button appearance={pv?.state === 'answerable' ? 'primary' : 'neutral'} onClick={() => onHarvest(q)}>
                  Responder
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

export function QuestionsPage() {
  const { toast } = useToast()
  const storeId = authService.getCurrentStoreId() || ''
  const [questions, setQuestions] = useState<Question[]>([])
  const [loading, setLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)
  const [text, setText] = useState('')
  const [notes, setNotes] = useState('')
  const [harvesting, setHarvesting] = useState<Question | null>(null)
  const [answerSummary, setAnswerSummary] = useState('')

  const load = async () => {
    try { setQuestions(await questionService.list(storeId)) }
    catch (e: any) { toast({ title: 'Error cargando preguntas', description: e?.message, variant: 'destructive' }) }
    finally { setLoading(false) }
  }
  useEffect(() => { if (storeId) load() }, [storeId])

  const add = async () => {
    if (!text.trim()) { toast({ title: 'Escribí la pregunta', variant: 'destructive' }); return }
    try {
      await questionService.create({ store_id: storeId, question: text.trim(), notes: notes.trim() || undefined })
      toast({ title: 'Pregunta creada', description: 'Queda Abierta hasta que le asignes un instrumento.' })
      setAddOpen(false); setText(''); setNotes('')
      load()
    } catch (e: any) { toast({ title: 'Error', description: e?.message, variant: 'destructive' }) }
  }

  const harvest = async () => {
    if (!harvesting) return
    try {
      await questionService.harvest(harvesting.question_id, answerSummary.trim() || undefined)
      toast({ title: 'Pregunta respondida' })
      setHarvesting(null); setAnswerSummary('')
      load()
    } catch (e: any) { toast({ title: 'Error', description: e?.message, variant: 'destructive' }) }
  }

  const kill = async (q: Question) => {
    try { await questionService.kill(q.question_id); toast({ title: 'Pregunta descartada' }); load() }
    catch (e: any) { toast({ title: 'Error', description: e?.message, variant: 'destructive' }) }
  }

  if (loading) return <div className="flex justify-center p-12"><Spinner size="large" /></div>

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Title as="h2">Preguntas</Title>
          <Text>Investigaciones que maduran con datos — el tablero de descubrimiento del CRO.</Text>
        </div>
        <Button appearance="primary" onClick={() => setAddOpen(true)}>
          <Icon source={<PlusIcon />} color="currentColor" /> Nueva pregunta
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {COLUMNS.map((col) => {
          const items = questions.filter(col.match)
          return (
            <div key={col.key} className="space-y-3">
              <div>
                <div className="flex items-center gap-2">
                  <Title as="h5">{col.title}</Title>
                  <Tag appearance="neutral">{items.length}</Tag>
                </div>
                <Text fontSize="caption">{col.hint}</Text>
              </div>
              {items.length === 0
                ? <div className="rounded-lg border border-dashed p-4 text-center"><Icon source={<QuestionCircleIcon />} /></div>
                : items.map((q) => (
                  <QuestionCard key={q.question_id} q={q} onHarvest={setHarvesting} onKill={kill} />
                ))}
            </div>
          )
        })}
      </div>

      {/* New question */}
      <Modal open={addOpen} onDismiss={() => setAddOpen(false)}>
        <Modal.Header title="Nueva pregunta" />
        <Modal.Body>
          <div className="space-y-3">
            <div>
              <Label htmlFor="q-text">Pregunta</Label>
              <Input id="q-text" value={text} onChange={(e) => setText(e.target.value)}
                placeholder="ej: ¿El ítem más clickeado del banner es el Pin de Sol?" />
            </div>
            <div>
              <Label htmlFor="q-notes">Notas (opcional)</Label>
              <Textarea id="q-notes" name="q-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Contexto, hipótesis, cómo se podría responder" />
            </div>
            <Text fontSize="caption">
              La pregunta nace Abierta. Cuando le asignes un instrumento (métrica), ancla su línea base
              en ese momento y pasa a Midiendo — los datos deciden cuándo es respondible.
            </Text>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="neutral" onClick={() => setAddOpen(false)}>Cancelar</Button>
          <Button appearance="primary" onClick={add}>Crear</Button>
        </Modal.Footer>
      </Modal>

      {/* Harvest */}
      <Modal open={!!harvesting} onDismiss={() => setHarvesting(null)}>
        <Modal.Header title="Responder pregunta" />
        <Modal.Body>
          <div className="space-y-3">
            <Text>{harvesting?.question}</Text>
            {harvesting?.preview?.verdict && VERDICT_LABEL[harvesting.preview.verdict] && (
              <Text fontSize="caption">
                Veredicto de los datos: <strong>{VERDICT_LABEL[harvesting.preview.verdict].label}</strong>
                {' '}(P(sin cambio) {(Number(harvesting.preview.p_inside ?? 0) * 100).toFixed(0)}%)
              </Text>
            )}
            <div>
              <Label htmlFor="q-answer">Resumen de la respuesta</Label>
              <Textarea id="q-answer" name="q-answer" value={answerSummary}
                onChange={(e) => setAnswerSummary(e.target.value)}
                placeholder="Qué aprendimos y qué acción dispara (si alguna)" />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="neutral" onClick={() => setHarvesting(null)}>Cancelar</Button>
          <Button appearance="primary" onClick={harvest}>Marcar respondida</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default QuestionsPage
